import React, { useState } from 'react'
import { Box, Paper, Stack, Typography, Divider, Tooltip, IconButton, useTheme, ClickAwayListener } from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import HubIcon from '@mui/icons-material/Hub'
import LinkIcon from '@mui/icons-material/Link'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import FactoryIcon from '@mui/icons-material/Factory'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import StorefrontIcon from '@mui/icons-material/Storefront'
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined'
import PersonIcon from '@mui/icons-material/Person'

const NODE_TYPES = [
  { key: 'supplier',    label: 'Suppliers',    Icon: WarehouseIcon,     color: '#10b981' },
  { key: 'factory',     label: 'Factories',    Icon: FactoryIcon,       color: '#f59e0b' },
  { key: 'distributor', label: 'Distributors', Icon: LocalShippingIcon, color: '#3b82f6' },
  { key: 'retailer',    label: 'Retailers',    Icon: StorefrontIcon,    color: '#8b5cf6' },
  { key: 'demand',      label: 'Customers',    Icon: PersonIcon,        color: '#f43f5e' },
]

function ToggleSwitch({ checked, color, isDark }) {
  return (
    <Box sx={{
      width: 32, height: 17, borderRadius: 8.5,
      bgcolor: checked ? color : (isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.15)'),
      position: 'relative', transition: 'background-color 0.22s ease',
      flexShrink: 0, boxShadow: checked ? `0 0 7px ${color}55` : 'none', pointerEvents: 'none',
    }}>
      <Box sx={{
        position: 'absolute', top: '50%',
        left: checked ? 'calc(100% - 15px)' : 2,
        transform: 'translateY(-50%)', width: 13, height: 13,
        borderRadius: '50%', bgcolor: '#fff',
        transition: 'left 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </Box>
  )
}

function VisibilityRow({ icon, label, checked, color, isDark, onToggle, hasBorder }) {
  return (
    <Box onClick={onToggle} sx={{
      display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 0.9,
      borderBottom: hasBorder ? `1px solid ${isDark ? 'rgba(255,255,255,0.045)' : 'rgba(0,0,0,0.045)'}` : 'none',
      cursor: 'pointer', transition: 'background 0.13s ease',
      '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)' },
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        bgcolor: `${color}1e`, border: `1px solid ${color}30`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, opacity: checked ? 1 : 0.4, transition: 'opacity 0.2s ease',
      }}>
        {icon}
      </Box>
      <Typography sx={{
        fontSize: '12px', fontWeight: 500, color: isDark ? '#e2e8f0' : '#374151',
        flex: 1, letterSpacing: '0.01em',
        opacity: checked ? 1 : 0.45, transition: 'opacity 0.18s ease',
      }}>
        {label}
      </Typography>
      <ToggleSwitch checked={checked} color={color} isDark={isDark} />
    </Box>
  )
}

function WorkspaceStats({ stats, onToggleFullscreen, isFullscreen, visibility, onToggleVisibility }) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const [panelOpen, setPanelOpen] = useState(false)

  const hasStats = stats.nodes > 0 || stats.links > 0 || stats.demands > 0
  if (!hasStats && !onToggleFullscreen && !visibility) return null

  const cardBg     = isDark ? 'rgba(15, 23, 42, 0.93)' : 'rgba(255, 255, 255, 0.96)'
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'
  const cardShadow = isDark ? '0 4px 16px rgba(0,0,0,0.4)' : '0 4px 16px rgba(0,0,0,0.1)'

  return (
    <ClickAwayListener onClickAway={() => setPanelOpen(false)}>
    <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
      {/* ── Single-line bar: stats | show | fullscreen ── */}
      <Paper elevation={0} sx={{
        bgcolor: cardBg, backdropFilter: 'blur(10px)',
        borderRadius: '10px', border: `1px solid ${cardBorder}`,
        px: 1.25, py: 0.65, boxShadow: cardShadow,
      }}>
        <Stack direction="row" spacing={1.1} alignItems="center">
          {hasStats && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55 }}>
                <HubIcon sx={{ fontSize: 13, color: 'primary.main' }} />
                <Typography sx={{ color: 'text.primary', fontSize: '0.78rem', fontWeight: 700, lineHeight: 1 }}>{stats.nodes}</Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', fontWeight: 500, lineHeight: 1 }}>nodes</Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ my: 0.3, opacity: 0.45 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55 }}>
                <LinkIcon sx={{ fontSize: 13, color: 'secondary.main' }} />
                <Typography sx={{ color: 'text.primary', fontSize: '0.78rem', fontWeight: 700, lineHeight: 1 }}>{stats.links}</Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', fontWeight: 500, lineHeight: 1 }}>links</Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ my: 0.3, opacity: 0.45 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55 }}>
                <ShoppingCartIcon sx={{ fontSize: 13, color: 'info.main' }} />
                <Typography sx={{ color: 'text.primary', fontSize: '0.78rem', fontWeight: 700, lineHeight: 1 }}>{stats.demands}</Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', fontWeight: 500, lineHeight: 1 }}>demands</Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ my: 0.3, opacity: 0.45 }} />
            </>
          )}

          {/* Show / visibility toggle */}
          {visibility && (
            <>
              <Tooltip title="Show" placement="bottom" arrow>
                <Box onClick={() => setPanelOpen(o => !o)} sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.15,
                  px: 0.4, py: 0.25, borderRadius: '6px', cursor: 'pointer',
                  color: panelOpen ? (isDark ? '#818cf8' : '#4f46e5') : (isDark ? '#94a3b8' : '#6b7280'),
                  bgcolor: panelOpen ? (isDark ? 'rgba(99,102,241,0.18)' : 'rgba(79,70,229,0.08)') : 'transparent',
                  transition: 'all 0.18s ease',
                  '&:hover': {
                    bgcolor: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.05)',
                    color: isDark ? '#e2e8f0' : '#374151',
                  },
                }}>
                  <VisibilityIcon sx={{ fontSize: 15 }} />
                  <motion.div animate={{ rotate: panelOpen ? 180 : 0 }} transition={{ duration: 0.2 }} style={{ display: 'flex', alignItems: 'center', lineHeight: 0 }}>
                    <KeyboardArrowDownIcon sx={{ fontSize: 11 }} />
                  </motion.div>
                </Box>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ my: 0.3, opacity: 0.45 }} />
            </>
          )}

          {/* Fullscreen — last */}
          {onToggleFullscreen && (
            <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} placement="bottom" arrow>
              <IconButton size="small" onClick={onToggleFullscreen} sx={{
                width: 24, height: 24, color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
              }}>
                {isFullscreen ? <FullscreenExitIcon sx={{ fontSize: 15 }} /> : <FullscreenIcon sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Paper>

      {/* ── Visibility dropdown ── */}
      <AnimatePresence>
        {panelOpen && visibility && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.96 }}
            transition={{ duration: 0.17, ease: [0.4, 0, 0.2, 1] }}
            style={{ transformOrigin: 'top right', position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 50 }}
          >
            <Box sx={{
              mt: 0.5, borderRadius: '11px', bgcolor: cardBg,
              border: `1px solid ${cardBorder}`,
              boxShadow: isDark ? '0 10px 36px rgba(0,0,0,0.5)' : '0 10px 36px rgba(0,0,0,0.13)',
              backdropFilter: 'blur(16px)', overflow: 'hidden', minWidth: 180,
            }}>
              {/* Node type rows */}
              {NODE_TYPES.map(({ key, label, Icon, color }, i) => (
                <VisibilityRow
                  key={key}
                  icon={<Icon sx={{ fontSize: 14, color }} />}
                  label={label}
                  checked={visibility[key] !== false}
                  color={color}
                  isDark={isDark}
                  onToggle={() => onToggleVisibility(key)}
                  hasBorder={i < NODE_TYPES.length - 1}
                />
              ))}

              <Box sx={{ height: '1px', bgcolor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)', mx: 1.25, my: 0.15 }} />

              {/* Labels */}
              <VisibilityRow
                icon={<Typography sx={{ fontSize: '9.5px', fontWeight: 800, color: isDark ? '#94a3b8' : '#6b7280', letterSpacing: '-0.02em', lineHeight: 1 }}>abc</Typography>}
                label="Labels"
                checked={visibility.labels !== false}
                color={isDark ? '#94a3b8' : '#6b7280'}
                isDark={isDark}
                onToggle={() => onToggleVisibility('labels')}
                hasBorder
              />

              {/* Connections */}
              <VisibilityRow
                icon={<LinkOutlinedIcon sx={{ fontSize: 14, color: isDark ? '#818cf8' : '#4f46e5' }} />}
                label="Connections"
                checked={visibility.connections !== false}
                color={isDark ? '#818cf8' : '#4f46e5'}
                isDark={isDark}
                onToggle={() => onToggleVisibility('connections')}
                hasBorder={false}
              />
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
    </ClickAwayListener>
  )
}

export default WorkspaceStats
